import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import PlusIcon from '@mui/icons-material/Add';
import { Counter, TextV2 } from '@insights-ltd/design-library/components';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import Notification from 'components/Notification';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useSearchContext } from 'components/SearchFilter/SearchProvider';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import {
  SEARCH_MODES,
  SearchMode,
  getMinimumSearchCharacters,
  getStartsWithContainsFilterOptions,
} from 'api/search';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import LearnersList from './LearnersList';
import LearnerSearchMessage from './LearnerSearchMessage';
import useGetLearners from './useGetLearners';

type LearnersTabProps = {
  organisationId?: string | null;
  handleOpenOrgSelector: () => void;
  notification?: string;
};

const LearnerSearchResults = ({
  organisationId,
  searchTerm,
  searchMode,
  minSearchCharacterLimit,
}: {
  organisationId?: string | null;
  searchTerm: string;
  searchMode: SearchMode;
  handleSearchMessageClick?: () => void;
  minSearchCharacterLimit: number;
}) => {
  const { t } = useTranslation();
  const { status, isFetched, paginator, apiPaginationProps } = useGetLearners(
    searchTerm,
    searchMode,
    organisationId,
  );

  const showWhenUnfiltered =
    !!organisationId && searchTerm?.length < minSearchCharacterLimit;

  const hasValidSearchTerm =
    searchTerm !== '' && searchTerm.length >= minSearchCharacterLimit;

  const searchResultsMessage = hasValidSearchTerm
    ? t('ui.event-management.learners.filtered-learners', {
        searchTerm,
      })
    : t('ui.event-management.learners.results-title');

  if (status === 'pending') {
    return (
      <FullScreenSpinner message={t('ui.event-management.loading')} inline />
    );
  }
  if (status === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.learners.error-loading-learners')}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          mt: (theme) => theme.spacing(spacingSizeMap.M),
          mb: (theme) => theme.spacing(spacingSizeMap.XS),
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Grid size={{ xs: 12, sm: 'auto' }}>
            {isFetched && (showWhenUnfiltered || hasValidSearchTerm) && (
              <TextV2 variant="h3">
                {searchResultsMessage}
                &nbsp;
                <Counter
                  count={paginator.totalCount}
                  size="large"
                  dataTestId="learner-count"
                />
              </TextV2>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <Button
              sx={{ padding: '0.5rem 1rem' }}
              variant="contained"
              color="primary"
              component={Link}
              to="/teams/create"
              startIcon={<PlusIcon />}
              fullWidth
            >
              {t('ui.event-management.teams.create-team')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      {paginator.totalCount > 0 ? (
        <LearnersList
          paginator={paginator}
          apiPaginationProps={apiPaginationProps}
        />
      ) : (
        isFetched &&
        searchTerm !== '' &&
        t('ui.event-management.learners.list.no-results')
      )}
    </>
  );
};

const LearnersTab = ({
  organisationId,
  handleOpenOrgSelector,
  notification,
}: LearnersTabProps) => {
  const { t } = useTranslation();
  const {
    searchState: { searchTerm = '', filter = 'STARTS_WITH' },
    setSearchState,
  } = useSearchContext<SearchMode>(SEARCH_MODES);
  const debouncedSearchTerm = useDebounceValue(searchTerm);

  const commitToSearch = (newSearchTerm: string) => {
    setSearchState({ searchTerm: newSearchTerm });
  };

  const ref = useRef<HTMLInputElement>(null);

  const handleSearchMessageClick = () => {
    ref.current?.focus();
  };

  const minSearchCharacterLimit = getMinimumSearchCharacters(filter);
  return (
    <>
      {!organisationId && searchTerm.length < minSearchCharacterLimit && (
        <LearnerSearchMessage
          i18nKey="ui.event-management.learners.search-limit-message"
          onClick={handleOpenOrgSelector}
        />
      )}
      <SearchFilter<{ leftSelectOptionsType: SearchMode }>
        labelText={`${t('ui.event-management.list-filter.search')} ${t(
          'ui.event-management.list-filter.by-name-email',
        )}`}
        placeholder={t('ui.event-management.learners.search.placeholder')}
        leftSelectProps={{
          options: getStartsWithContainsFilterOptions(t),
          onChange: (value: SearchMode) => {
            setSearchState({ searchTerm, filter: value });
          },
          value: filter,
        }}
        handleTermChange={commitToSearch}
        initialValue={searchTerm}
        inputState={searchTerm}
        allowEmptySearches={false}
        showButton={false}
        minCharacters={minSearchCharacterLimit}
        ref={ref}
        isLabelAboveSearchInput
      />
      {notification && <Notification message={notification} />}
      <LearnerSearchResults
        organisationId={organisationId}
        searchTerm={debouncedSearchTerm}
        searchMode={filter as SearchMode}
        handleSearchMessageClick={handleSearchMessageClick}
        minSearchCharacterLimit={minSearchCharacterLimit}
      />
    </>
  );
};

export default LearnersTab;
